import React, { useContext } from 'react';
import { BaseRendererProps } from './eventRenderers';
import GameAssetsContext from '../../../../../../context/GameAssetContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTurnUp } from '@fortawesome/free-solid-svg-icons';
import { getUniversalSourceIcon } from './StatChangeEvent';
import './DamageMitigationEvent.css';

const DamageMitigationEvent: React.FC<BaseRendererProps> = ({ log }) => {
  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);

  const details = log.details;
  const {
    championName,
    attackerName,
    damageReduced,
    mitigationSource,
    mitigationType,
    additionalInfo,
  } = details;

  // Get damage type from additionalInfo if available
  const damageType = additionalInfo?.damageType || null;

  // Use the universal image fetching function for the source icon
  const sourceIcon = getUniversalSourceIcon(
    {
      sourceType: mitigationType === 'rune' ? 'Rune' : 
                 mitigationType === 'item' ? 'Item' : 
                 mitigationType === 'ability' ? 'Ability' : 'Ability',
      imageState: log.imageState,
      sourceName: mitigationSource,
      championName: championName,
    },
    gameWideAssets,
    championsAssets
  );

  // Display the amount of damage reduced
  const damageDisplay = `-${Math.abs(damageReduced).toFixed(0)}`;

  // Determine the damage reduction icon based on damage type
  let damageReductionIcon = null;
  if (damageType === 'Physical') {
    damageReductionIcon = (
      <div className="damage-mitigation-icon-container">
        <img 
          src={gameWideAssets.damageReductionIcon} 
          alt="Physical Damage Reduction" 
          className="damage-mitigation-icon physical-damage-reduction"
        />
        <span className="damage-reduction-arrow">↓</span>
      </div>
    );
  } else if (damageType === 'Magic') {
    damageReductionIcon = (
      <div className="damage-mitigation-icon-container">
        <img 
          src={gameWideAssets.damageReductionIcon} 
          alt="Magic Damage Reduction" 
          className="damage-mitigation-icon magic-damage-reduction"
        />
        <span className="damage-reduction-arrow">↓</span>
      </div>
    );
  } else {
    // Default damage reduction icon (for mixed or unspecified damage types)
    damageReductionIcon = (
      <div className="damage-mitigation-icon-container">
        <img 
          src={gameWideAssets.damageReductionIcon} 
          alt="Damage Reduction" 
          className="damage-mitigation-icon"
        />
        <span className="damage-reduction-arrow">↓</span>
      </div>
    );
  }

  // Additional info for tooltip (specific to each mitigation type)
  const getAdditionalTooltipInfo = () => {
    if (additionalInfo && mitigationSource === 'Bone Plating') {
      const { hitsRemaining, totalHitsToBlock } = additionalInfo;
      return `. ${hitsRemaining}/${totalHitsToBlock} hits left`;
    }
    if (additionalInfo && mitigationSource === 'Unbreakable Will') {
      const { damageReductionPercent } = additionalInfo;
      return `. ${damageReductionPercent}% damage reduction`;
    }
    if (additionalInfo && mitigationSource === 'Shield of Durand') {
      const { damageReductionPercent } = additionalInfo;
      return `. ${damageReductionPercent}% damage reduction`;
    }
    return '';
  };

  // Tooltip text with damage type information
  const damageTypeText = damageType ? ` ${damageType.toLowerCase()}` : '';
  const tooltipText = `${mitigationSource} reduced ${damageDisplay}${damageTypeText} damage from ${attackerName}${getAdditionalTooltipInfo()}`;

  return (
    <div className="damage-mitigation-event" title={tooltipText}>
      <div className="damage-mitigation-amount-wrapper">
        {damageReductionIcon}
        <span className="damage-mitigation-amount">{damageDisplay}</span>
      </div>
      <div className="damage-mitigation-source-wrapper">
        {sourceIcon && (
          <>
            <FontAwesomeIcon icon={faTurnUp} className="fa-turn-up" />
            {sourceIcon}
          </>
        )}
      </div>
    </div>
  );
};

export default DamageMitigationEvent; 